import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAllSearchParams } from 'storfox-route-hooks'
import { prop } from 'ramda'
import { useDeepCompareEffect } from 'storfox-api-hooks'
import { useSnackbar } from 'storfox-snackbar'

import { escapeAtob, unescapeBtoa } from '~/utils'
import { DashboardLayout } from '~/components/Layouts'
import * as NAV from '~/constants/nav-titles'
import * as ROUTES from '~/constants/routes'
import {
  PRODUCT_BULK_UPLOAD_CREATE_COMPLETE,
  PRODUCT_BULK_UPLOAD_CREATE_STATUS,
  PRODUCT_BULK_UPLOAD_VALIDATION_STATUS,
  PRODUCT_BULK_UPLOAD_VALIDATION_COMPLETE
} from '~/constants/notification-topics'
import { useProgressCard } from '~/components/ProgressCard'
import { useNotification } from '~/components/Notification'
import { useGoogleEvent } from '~/components/GoogleAnalytics/GoogleAnalytics'
import useMessages from '~/hooks/useMessages'

import ProductImportForm from '../components/ProductImport'
import { useProductImport } from '../hooks'

function ProductImportContainer () {
  const productImport = useProductImport()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const messages = useMessages()
  const { sendEvent } = useGoogleEvent()
  const { payload } = useAllSearchParams()

  const progressCard = useProgressCard()

  const validateStatus = useNotification(PRODUCT_BULK_UPLOAD_VALIDATION_STATUS)
  const validateComplete = useNotification(PRODUCT_BULK_UPLOAD_VALIDATION_COMPLETE)
  const uploadComplete = useNotification(PRODUCT_BULK_UPLOAD_CREATE_COMPLETE)
  const uploadStatus = useNotification(PRODUCT_BULK_UPLOAD_CREATE_STATUS)

  const key = prop('key', escapeAtob(payload))

  useDeepCompareEffect(() => {
    if (key) {
      validateStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const validated = prop('totalValidated', result)
        const total = prop('total', result)

        progressCard.handleValidationChange({ current: validated, total })
      })

      validateComplete.subscribe(key, ({ payload }) => {
        navigate(`${ROUTES.PRODUCT_IMPORT_PATH}?payload=${unescapeBtoa(payload)}`, { replace: true })

        validateComplete.setLoading(false)
      })

      uploadStatus.subscribe(key, ({ payload }) => {
        const result = prop('result', payload)
        const completed = prop('totalCreated', result)
        const total = prop('total', result)
        if (!uploadComplete.isLoading) {
          uploadComplete.setLoading(true)
          progressCard.handleValidationChange({ current: total, total })
        }
        progressCard.handleCompletionChange({ current: completed, total })
      })

      uploadComplete.subscribe(key, () => {
        navigate(ROUTES.PRODUCT_LIST_PATH, { replace: true })
        uploadComplete.setLoading(false)
        sendEvent({ eventAction: 'Upload File Product', eventCategory: 'Product' })
      })
    }

    return () => {
      validateStatus.unsubscribe()
      validateComplete.unsubscribe()
      uploadStatus.unsubscribe()
      uploadComplete.unsubscribe()
    }
  }, [key])

  const handleSubmit = () => {
    uploadComplete.setLoading(true)
    return productImport.create({ key })
      .then(() => snackbar({ message: messages.IMPORT_REQUEST_SUCCESS }))
  }

  const handleSubscribe = key => {
    snackbar({ message: messages.NOTIFICATION_WAIT })
    validateComplete.setLoading(true)
    navigate(`${ROUTES.PRODUCT_IMPORT_PATH}?payload=${unescapeBtoa({ key })}`, { replace: true })
    sendEvent({ eventAction: 'Validate File Product', eventCategory: 'Product' })
  }

  const handleFileRemove = () => {
    validateComplete.unsubscribe()
    uploadComplete.unsubscribe()
    validateStatus.unsubscribe()
    uploadStatus.unsubscribe()
    progressCard.resetValues()
  }

  const title = 'Import Products / Update Products'

  const isLoading = (
    productImport.isLoading ||
    validateComplete.isLoading ||
    uploadComplete.isLoading
  )

  const breadcrumbs = { title }

  return (
    <DashboardLayout
      title={title}
      activeNav={NAV.PRODUCTS}
      isLoading={validateComplete.isLoading || uploadComplete.isLoading}
      breadcrumbs={breadcrumbs}
    >
      <ProductImportForm
        title={title}
        isLoading={isLoading}
        progressCard={progressCard}
        onSubmit={handleSubmit}
        onSubscribe={handleSubscribe}
        onFileRemove={handleFileRemove}
      />
    </DashboardLayout>
  )
}

export default ProductImportContainer
